// drafts.ts — the app's LIGHT local draft buffer (localStorage). Replaces the heavy SQLite pipeline:
// connections + the actual posting now live SERVER-side (the Connections hub + /post), so the app holds
// only the in-progress drafts it's working on. No tokens, no schema, no SQLite — this is a light shell.

export type PlatformId = 'mastodon' | 'bluesky' | 'discord' | 'telegram' | 'x';
export type DraftStatus = 'draft' | 'posted' | 'failed';

export interface LocalDraft {
  id: string;
  platform: PlatformId;
  content: string;
  topic?: string | null;
  ai_disclosure?: boolean;
  status: DraftStatus;
  post_url?: string;
  error?: string;
  created_at: string;
}

const KEY = 'oriora_social_drafts';

function read(): LocalDraft[] {
  try { return JSON.parse(localStorage.getItem(KEY) || '[]') as LocalDraft[]; } catch { return []; }
}
function write(list: LocalDraft[]): void {
  try { localStorage.setItem(KEY, JSON.stringify(list)); } catch { /* storage blocked → in-session only */ }
}

export function listDrafts(): LocalDraft[] { return read(); }
export function getDraft(id: string): LocalDraft | undefined { return read().find((d) => d.id === id); }

export function addDraft(d: { platform: PlatformId; content: string; topic?: string | null; ai_disclosure?: boolean }): LocalDraft {
  const draft: LocalDraft = { ...d, id: crypto.randomUUID(), status: 'draft', created_at: new Date().toISOString() };
  write([draft, ...read()]);
  return draft;
}
export function updateDraft(id: string, patch: Partial<LocalDraft>): void {
  write(read().map((d) => (d.id === id ? { ...d, ...patch } : d)));
}
export function removeDraft(id: string): void {
  write(read().filter((d) => d.id !== id));
}

// ── Chat persistence (local-only, same principle as drafts) ────────────────────────────────────
// The chat lives on this device only — no Oriora-side store, no persistence promise. It survives a
// refresh, and is lost on clear/reinstall. The customer can export/import it (below) to keep or move it.
const CHAT_KEY = 'oriora_social_chat';
export interface StoredTurn { role: 'user' | 'assistant'; content: string }

export function loadChat(): StoredTurn[] {
  try { return JSON.parse(localStorage.getItem(CHAT_KEY) || '[]') as StoredTurn[]; } catch { return []; }
}
export function saveChat(turns: StoredTurn[]): void {
  try { localStorage.setItem(CHAT_KEY, JSON.stringify(turns)); } catch { /* storage blocked → in-session only */ }
}
export function clearChat(): void {
  try { localStorage.removeItem(CHAT_KEY); } catch { /* ignore */ }
}

// ── Personality profile (local-only; the voice the brain writes every post in) ──────────────────
// Same principle as everything else here: lives on this device, sent as context per call, never stored
// by Oriora. Survives a refresh; travels in the export below so it lasts through app updates.
const PERSONALITY_KEY = 'oriora_social_personality';
export interface Personality {
  identity?: string;  // who you are / your brand
  tone?: string;      // casual, punchy, warm, formal…
  audience?: string;  // who you're talking to
  topics?: string;    // what you post about
  never_say?: string; // words/claims you never use
  examples?: string;  // a few posts that sound like you
}
export function loadPersonality(): Personality {
  try { return JSON.parse(localStorage.getItem(PERSONALITY_KEY) || '{}') as Personality; } catch { return {}; }
}
export function savePersonality(p: Personality): void {
  try { localStorage.setItem(PERSONALITY_KEY, JSON.stringify(p)); } catch { /* storage blocked → in-session only */ }
}

// ── Export / import — the customer keeps and moves their OWN data; Oriora stores nothing. ───────
export function exportData(): string {
  return JSON.stringify(
    { app: 'oriora-content', exported_at: new Date().toISOString(), personality: loadPersonality(), drafts: read(), chat: loadChat() },
    null, 2,
  );
}
export function importData(json: string): { drafts: number; chat: number } {
  const d = JSON.parse(json) as { drafts?: LocalDraft[]; chat?: StoredTurn[]; personality?: Personality };
  if (d.personality && typeof d.personality === 'object') savePersonality(d.personality);
  if (Array.isArray(d.drafts)) write(d.drafts);
  if (Array.isArray(d.chat)) saveChat(d.chat);
  return { drafts: d.drafts?.length ?? 0, chat: d.chat?.length ?? 0 };
}
